/**
 * Turn a server feature/plan gate refusal into a clear, user-facing prompt
 * instead of a raw "403: …" string. apiRequest errors carry `status` and the
 * JSON body in `data` (lib/queryClient throwIfResNotOk).
 *
 * Recognised shapes:
 *  - requireFeature (middleware/featureGating): { upgradeRequired, message }
 *  - requirePaidPlan (services/planGate): { code: PLAN_REQUIRED | TRIAL_NO_PURCHASE | PLAN_CANCELED, requiresUpgrade }
 *  - recording gate (lib/securityGates): { code: FEATURE_NOT_IN_PLAN, feature }
 *  - phone-number limit (enforcePhoneNumberLimits): { errorCode: 'limit_reached', upgradeRequired }
 *  - PLAN_CHECK_UNAVAILABLE (503): transient, "try again"
 */
export interface GatePrompt {
  title: string;
  message: string;
  /** Show an Upgrade action (link to /settings/billing). */
  upgrade: boolean;
}

const PLAN_CODES = new Set(['PLAN_REQUIRED', 'TRIAL_NO_PURCHASE', 'PLAN_CANCELED', 'FEATURE_NOT_IN_PLAN']);

export function gateErrorPrompt(error: unknown): GatePrompt | null {
  const e = error as any;
  if (!e) return null;
  const data = e.data && typeof e.data === 'object' ? e.data : (typeof e === 'object' ? e : {});
  const code: string = data.code || data.errorCode || e.code || '';
  const message: string = data.message || '';

  if (code === 'PLAN_CHECK_UNAVAILABLE') {
    return { title: 'Please try again', message: message || 'We could not verify your plan right now. Please try again in a moment.', upgrade: false };
  }
  if (code === 'limit_reached') {
    return { title: 'Upgrade to add more numbers', message: message || 'Your plan has reached its phone number limit.', upgrade: true };
  }
  if (PLAN_CODES.has(code) || data.upgradeRequired === true || data.requiresUpgrade === true) {
    const title = data.feature === 'callRecording' ? 'Recording needs an upgrade'
      : /ai|receptionist|voice agent|aiVoice/i.test(message) ? 'AI Receptionist needs an upgrade'
      : 'Upgrade required';
    return { title, message: message || 'This feature is not included in your current plan.', upgrade: data.upgradeRequired !== false };
  }
  return null;
}
